/**
 * Customize a theme, as in AgencyAnalytics: Background (a colour, or an image
 * shown Fit or Cover), Typography (title and body fonts) and Colors (cards,
 * text, accent and the chart series). Every change applies at once.
 */

import { useState, type ReactNode } from 'react';

import { ACCEPTED, MAX_BYTES, readAsDataUrl } from './ImageFields.tsx';
import { PRESET_THEMES, THEME_FONTS, type ReportTheme, type ThemeFont } from './report-theme.ts';

const BASE = PRESET_THEMES[0] as ReportTheme;
const COLOURS = [
  ['card_color', 'Widget background'],
  ['text_color', 'Text'],
  ['accent_color', 'Accent'],
] as const;

function ColourRow(props: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="rth-row">
      {props.label}
      <input
        type="color"
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
      />
    </label>
  );
}

function FontRow(props: { label: string; value: ThemeFont; onChange: (value: ThemeFont) => void }) {
  return (
    <label className="rs-field">
      {props.label}
      <select
        value={props.value}
        onChange={(event) => props.onChange(event.target.value as ThemeFont)}
      >
        {THEME_FONTS.map((font) => (
          <option key={font} value={font} style={{ fontFamily: font }}>
            {font}
          </option>
        ))}
      </select>
    </label>
  );
}

export function ThemeCustomize({
  theme,
  onTheme,
}: {
  theme: ReportTheme;
  onTheme: (theme: ReportTheme) => void;
}): ReactNode {
  const [problem, setProblem] = useState<string | null>(null);
  const value = { ...BASE, ...theme };
  const set = (patch: Partial<ReportTheme>) => {
    const { name: _name, ...rest } = { ...value, ...patch };
    onTheme({ ...rest, preset: 'custom' });
  };
  const series = value.series ?? [];
  const pickImage = async (file: File | undefined) => {
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) return setProblem('Use a PNG, JPEG or WebP image.');
    if (file.size > MAX_BYTES) return setProblem('The image must be 300 KB or smaller.');
    setProblem(null);
    set({
      background_image: await readAsDataUrl(file),
      background_fit: value.background_fit ?? 'cover',
    });
  };
  const usingImage = Boolean(value.background_image);

  return (
    <div className="rth">
      <fieldset>
        <legend>Background</legend>
        <div className="rb-seg" role="group" aria-label="Background type">
          <button
            type="button"
            className={usingImage ? '' : 'on'}
            aria-pressed={!usingImage}
            onClick={() => {
              const { background_image: _image, background_fit: _fit, ...rest } = value;
              onTheme({ ...rest, preset: 'custom' });
            }}
          >
            Color
          </button>
          <label className={`rth-upload${usingImage ? ' on' : ''}`}>
            Image
            <input
              type="file"
              accept={ACCEPTED.join(',')}
              className="sr-only"
              onChange={(event) => void pickImage(event.target.files?.[0])}
            />
          </label>
        </div>
        {problem && (
          <p className="rh-access-msg rh-access-error" role="alert">
            {problem}
          </p>
        )}
        {usingImage ? (
          <label className="rs-field">
            Image fit
            <select
              value={value.background_fit ?? 'cover'}
              onChange={(event) => set({ background_fit: event.target.value as 'fit' | 'cover' })}
            >
              <option value="cover">Cover</option>
              <option value="fit">Fit</option>
            </select>
          </label>
        ) : (
          <ColourRow
            label="Background color"
            value={value.background_color ?? '#ffffff'}
            onChange={(background_color) => set({ background_color })}
          />
        )}
      </fieldset>
      <fieldset>
        <legend>Typography</legend>
        <FontRow
          label="Title font"
          value={value.title_font ?? 'Inter'}
          onChange={(title_font) => set({ title_font })}
        />
        <FontRow
          label="Body font"
          value={value.body_font ?? 'Inter'}
          onChange={(body_font) => set({ body_font })}
        />
      </fieldset>
      <fieldset>
        <legend>Colors</legend>
        {COLOURS.map(([key, label]) => (
          <ColourRow
            key={key}
            label={label}
            value={value[key] ?? '#000000'}
            onChange={(colour) => set({ [key]: colour })}
          />
        ))}
        <span>Chart colors</span>
        <div className="rth-series">
          {series.map((colour, index) => (
            <input
              key={index}
              type="color"
              aria-label={`Chart color ${index + 1}`}
              value={colour}
              onChange={(event) =>
                set({
                  series: series.map((item, at) => (at === index ? event.target.value : item)),
                })
              }
            />
          ))}
        </div>
      </fieldset>
    </div>
  );
}
